<script setup>
    import { computed, ref, watch } from 'vue'
    import { InputHeader } from '@/js/components/Common'
    import { useEditStateStore } from '@/js/stores/editState'

    const props = defineProps({
        gefoerderteKompetenzen: {
            type: Object,
            required: true
        },
        allowEdit: {
            type: Boolean,
            required: false,
            default: false
        },
        tooltip: {
            type: String,
            required: false,
            default: () => ''
        },
        language: {
            type: String,
            required: false,
            default: () => 'deutsch'
        }
    })

    const title = computed(() =>
        props.language === 'englisch'
            ? 'Competences | Im Modul geförderte Kompetenzen'
            : 'Im Modul geförderte Kompetenzen'
    )

    const emit = defineEmits(['gefoerderteKompetenzenSaved'])

    const fach = ref(props.gefoerderteKompetenzen.fachkompetenz)
    const methoden = ref(props.gefoerderteKompetenzen.methodenkompetenz)
    const sozial = ref(props.gefoerderteKompetenzen.sozialkompetenz)
    const selbst = ref(props.gefoerderteKompetenzen.selbstkompetenz)
    const isLoading = computed(() => store.getters['isLoading'])

    const ratingOptions = {
        iconEmpty: 'mdi-square-outline',
        iconHalf: 'square-half',
        iconFull: 'mdi-square',
        color: 'grey'
    }

    const editStore = useEditStateStore()
    const editKey = 'gefoerderteKompetenzen'
    editStore.register([editKey])
    const isEditing = computed(() => editStore.editStates[editKey])

    function startEditing() {
        editStore.set(editKey, true)
    }

    function stopEditing() {
        editStore.set(editKey, false)
        resetLocalKompetenzen()
    }

    const handleEditClick = () => {
        if (isLoading.value) {
            return
        }
        startEditing()
    }

    const handleAbortClick = () => {
        if (isLoading.value) {
            return
        }
        resetLocalKompetenzen()
        stopEditing()
    }

    const resetLocalKompetenzen = () => {
        fach.value = props.gefoerderteKompetenzen.fachkompetenz
        methoden.value = props.gefoerderteKompetenzen.methodenkompetenz
        sozial.value = props.gefoerderteKompetenzen.sozialkompetenz
        selbst.value = props.gefoerderteKompetenzen.selbstkompetenz
    }

    watch(() => props.gefoerderteKompetenzen, resetLocalKompetenzen)

    const saveGefoerderteKompetenzen = () => {
        emit('gefoerderteKompetenzenSaved', {
            fachkompetenz: fach.value,
            methodenkompetenz: methoden.value,
            sozialkompetenz: sozial.value,
            selbstkompetenz: selbst.value
        })
        stopEditing()
    }
</script>

<template>
    <v-form novalidate @submit.prevent>
        <div class="d-flex flex-nowrap align-stretch">
            <input-header :title="title" :tool-tip="tooltip" />
            <div
                class="h-auto rounded-0 d-flex align-center pa-2 input-entry-show w-100 border-primary border border-opacity-100"
            >
                <div class="d-flex flex-column flex-nowrap rating-container">
                    <label for="rating-method">{{
                        language === 'englisch' ? 'Professional competence' : 'Fachkompetenz'
                    }}</label>
                    <div class="d-flex align-center border-primary border-lg rounded-lg border-opacity-25 text-grey">
                        <v-rating
                            id="rating-method"
                            v-model="fach"
                            :readonly="!isEditing"
                            :color="ratingOptions.color"
                            :empty-icon="ratingOptions.iconEmpty"
                            :full-icon="ratingOptions.iconFull"
                        />
                        <b>{{ fach }} / 5</b>
                    </div>
                    <label for="rating-fach" class="mt-2">
                        {{ language === 'englisch' ? 'Methodological competence' : 'Methodenkompetenz' }}
                    </label>
                    <div class="d-flex align-center border-primary border-lg rounded-lg border-opacity-25 text-grey">
                        <v-rating
                            id="rating-fach"
                            v-model="methoden"
                            :readonly="!isEditing"
                            :color="ratingOptions.color"
                            :empty-icon="ratingOptions.iconEmpty"
                            :full-icon="ratingOptions.iconFull"
                        />
                        <b>{{ methoden }} / 5</b>
                    </div>
                    <label for="rating-sozial" class="mt-2">{{
                        language === 'englisch' ? 'Social competence' : 'Sozialkompetenz'
                    }}</label>
                    <div class="d-flex align-center border-primary border-lg rounded-lg border-opacity-25 text-grey">
                        <v-rating
                            id="rating-sozial"
                            v-model="sozial"
                            :readonly="!isEditing"
                            :color="ratingOptions.color"
                            :empty-icon="ratingOptions.iconEmpty"
                            :full-icon="ratingOptions.iconFull"
                        />
                        <b>{{ sozial }} / 5</b>
                    </div>
                    <label for="rating-selbst" class="mt-2">{{
                        language === 'englisch' ? 'Personal competence' : 'Selbstkompetenz'
                    }}</label>
                    <div class="d-flex align-center border-primary border-lg rounded-lg border-opacity-25 text-grey">
                        <v-rating
                            id="rating-selbst"
                            v-model="selbst"
                            :readonly="!isEditing"
                            :color="ratingOptions.color"
                            :empty-icon="ratingOptions.iconEmpty"
                            :full-icon="ratingOptions.iconFull"
                        />
                        <b>{{ selbst }} / 5</b>
                    </div>
                </div>
            </div>
            <v-btn
                v-if="allowEdit && !isEditing"
                icon="mdi-lead-pencil"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="handleEditClick"
            />
            <v-btn
                v-if="allowEdit && isEditing"
                icon="mdi-check"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="saveGefoerderteKompetenzen"
            />
            <v-btn
                v-if="allowEdit && isEditing"
                icon="mdi-cancel"
                border
                height="inherit"
                class="border-primary border-opacity-100 text-grey"
                color="primary-lighten-2"
                tile
                variant="flat"
                @click="handleAbortClick"
            />
        </div>
    </v-form>
</template>

<script></script>

<style scoped lang="scss">
    label {
        margin-bottom: 0 !important;
    }

    .input-entry-show {
        white-space: pre-line;
    }

    .rating-container {
        width: 300px;
    }
</style>
